/// <reference types="react-scripts" />
import React, { useState, useEffect, Suspense, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { PasswordModal } from '../components/ProfileModals';
import { StatsCard } from '../components/Dashboard/StatsCard';
import { MenuPreview } from '../components/Dashboard/MenuPreview';
import { TenantBranding } from '../types/branding';
import { usePermissions } from '../contexts/PermissionsContext';
import { useFeatures } from '../contexts/FeatureContext';
// Caricamento dinamico per ridurre problemi di risoluzione e supportare code splitting
// @ts-ignore
const _BrandPreviewImport = () => import('../components/BrandPreview');
const _BrandEditorImport = () => import('../components/BrandEditor');
const BrandPreview = React.lazy(_BrandPreviewImport);
const BrandEditor = React.lazy(_BrandEditorImport);
import '../components/BrandPreview.css';

export function Dashboard() {
    const [forceChangePwd, setForceChangePwd] = useState<boolean>(localStorage.getItem('mustChangePassword') === '1');
    const [branding, setBranding] = useState<TenantBranding | null>(null);
    const [editing, setEditing] = useState(false);
    const [loadingBrand, setLoadingBrand] = useState(true);
    const [brandingError, setBrandingError] = useState<string | null>(null);
    const [etag, setEtag] = useState<string | null>(null);
    const [brandingEnabled, setBrandingEnabled] = useState<boolean>(true);
    const { canAccess, isTenantAdmin } = usePermissions();
    const { features } = useFeatures();

    const quickActions = useMemo(() => {
        type PermissionKey = Parameters<typeof canAccess>[0];
        type QuickAction = {
            key: string;
            label: string;
            to: string;
            background: string;
            permission?: PermissionKey;
            requireFeature?: () => boolean;
        };

        const baseActions: QuickAction[] = [
            {
                key: 'translation',
                label: '🌐 Traduzione Menu',
                to: '/translation',
                background: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
                permission: 'staffCanManageMenu',
                requireFeature: () => features.multiLanguageEnabled || isTenantAdmin
            },
            {
                key: 'menu-management',
                label: '📝 Gestione Menu',
                to: '/menu-management',
                background: 'linear-gradient(135deg, #27ae60 0%, #229954 100%)',
                permission: 'staffCanManageMenu'
            },
            {
                key: 'events',
                label: '🎉 Eventi & Feste',
                to: '/events',
                background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
                permission: 'staffCanManageEvents'
            }
        ];

        return baseActions.filter(action => {
            if (action.permission && !canAccess(action.permission)) {
                return false;
            }
            if (action.requireFeature && !action.requireFeature()) {
                return false;
            }
            return true;
        });
    }, [features, canAccess, isTenantAdmin]);

    const loadBranding = useCallback(async () => {
        setLoadingBrand(true);
        setBrandingError(null);
        try {
            // Importa apiFetch per utilizzare l'autenticazione e l'intestazione del tenant
            const { apiFetch } = await import('../api/client');
            const res = await apiFetch('/api/tenant/self/branding', {
                credentials: 'include',
                headers: {
                    'Accept': 'application/json',
                    ...(etag ? { 'If-None-Match': etag } : {})
                }
            });
            if (res.status === 304) {
                // Not modified: keep current branding
                setLoadingBrand(false);
                return;
            } else if (res.status === 204) {
                setBranding(null);
            } else if (!res.ok) {
                throw new Error('HTTP ' + res.status);
            } else {
                const data = await res.json();
                setBranding(data);
                const newEtag = res.headers.get('ETag');
                if (newEtag) setEtag(newEtag);
            }
        } catch (e: any) {
            setBrandingError(e.message || 'Errore caricamento branding');
        } finally {
            setLoadingBrand(false);
        }
    }, [etag]);

    useEffect(() => { loadBranding(); }, [loadBranding]);

    // Carica feature del tenant per capire se mostrare il blocco Branding
    useEffect(() => {
        (async () => {
            try {
                const { apiFetch } = await import('../api/client');
                const res = await apiFetch('/api/tenant/self/features');
                if (res.ok) {
                    const f = await res.json();
                    setBrandingEnabled(!!f.brandingEnabled);
                }
            } catch (_) { /* ignore, default true */ }
        })();
    }, []);

    return (
        <>
            {forceChangePwd && (
                <PasswordModal onClose={() => { localStorage.removeItem('mustChangePassword'); setForceChangePwd(false); }} />
            )}
            <div className="dashboard-grid">
                <StatsCard title="Ordini oggi" value="23" trend="+5%" />
                <StatsCard title="Clienti attivi" value="12" trend="+2%" />
                <StatsCard title="Prodotti" value="54" trend="+1%" />
                <StatsCard title="Locali" value="3" trend="0%" />

                {/* Quick Actions Card */}
                <div className="card" style={{ gridColumn: '1 / 3' }}>
                    <h3>🚀 Azioni Rapide</h3>
                    {quickActions.length > 0 ? (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginTop: '15px' }}>
                            {quickActions.map(action => (
                                <Link key={action.key} to={action.to} style={{ textDecoration: 'none' }}>
                                    <button
                                        style={{
                                            width: '100%',
                                            padding: '15px 20px',
                                            background: action.background,
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            fontWeight: 'bold',
                                            fontSize: '14px',
                                            transition: 'transform 0.2s ease'
                                        }}
                                        onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
                                        onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
                                    >
                                        {action.label}
                                    </button>
                                </Link>
                            ))}
                        </div>
                    ) : (
                        <p style={{ marginTop: '12px', color: '#6c757d' }}>
                            Nessuna azione rapida disponibile con i permessi o le funzionalità attuali.
                        </p>
                    )}
                </div>

                <div className="card">
                    <h3>Menu Preview</h3>
                    <MenuPreview />
                </div>
                {brandingEnabled && (
                    <div className="card" style={{ gridColumn: '1 / -1' }}>
                        <h3>Branding</h3>
                        <Suspense fallback={<div>Caricamento componente branding...</div>}>
                            {!editing && (
                                <BrandPreview
                                    branding={branding}
                                    loading={loadingBrand}
                                    error={brandingError}
                                    onEdit={() => setEditing(true)}
                                />
                            )}
                            {editing && (
                                <BrandEditor
                                    initial={branding || undefined}
                                    onCancel={() => setEditing(false)}
                                    onSave={async (draft) => {
                                        try {
                                            const { apiFetch } = await import('../api/client');

                                            const upload = async (file?: File | null) => {
                                                if (!file) return undefined;
                                                const formData = new FormData();
                                                formData.append('file', file);
                                                const uploadResponse = await apiFetch('/api/upload', {
                                                    method: 'POST',
                                                    body: formData
                                                });
                                                if (!uploadResponse.ok) {
                                                    let message = 'Upload fallito';
                                                    try {
                                                        const text = await uploadResponse.text();
                                                        if (text) {
                                                            try {
                                                                const parsed = JSON.parse(text);
                                                                message = parsed.message || parsed.error || text;
                                                            } catch {
                                                                message = text;
                                                            }
                                                        }
                                                    } catch {
                                                        /* ignore */
                                                    }
                                                    throw new Error(message);
                                                }
                                                const payload = await uploadResponse.json();
                                                return payload.url as string;
                                            };

                                            const [logoUploadedUrl, bgUploadedUrl] = await Promise.all([
                                                upload(draft.logoFile),
                                                upload(draft.backgroundFile)
                                            ]);

                                            const isBlobUrl = (value?: string) => Boolean(value && value.startsWith('blob:'));

                                            const payload: TenantBranding = {
                                                name: draft.name.trim(),
                                                description: draft.description?.trim() || undefined,
                                                primaryColor: draft.primaryColor,
                                                secondaryColor: draft.secondaryColor,
                                                backgroundColor: draft.backgroundColor,
                                                textColor: draft.textColor,
                                                fontFamily: draft.fontFamily,
                                                logoUrl: logoUploadedUrl ?? (!isBlobUrl(draft.logoUrl) ? draft.logoUrl : undefined),
                                                backgroundImageUrl: bgUploadedUrl ?? (!isBlobUrl(draft.backgroundImageUrl) ? draft.backgroundImageUrl : undefined)
                                            };

                                            const res = await apiFetch('/api/tenant/self/branding', {
                                                method: 'PUT',
                                                credentials: 'include',
                                                headers: { 'Content-Type': 'application/json' },
                                                body: JSON.stringify(payload)
                                            });

                                            if (!res.ok) {
                                                let message = 'Salvataggio fallito';
                                                try {
                                                    const text = await res.text();
                                                    if (text) {
                                                        try {
                                                            const parsed = JSON.parse(text);
                                                            message = parsed.message || parsed.error || text;
                                                        } catch {
                                                            message = text;
                                                        }
                                                    }
                                                } catch {
                                                    /* ignore */
                                                }
                                                throw new Error(message);
                                            }

                                            const saved = await res.json();
                                            setBranding(saved);
                                            const newEtag = res.headers.get('ETag');
                                            if (newEtag) setEtag(newEtag);
                                            setEditing(false);
                                        } catch (e: any) {
                                            setBrandingError(e?.message || 'Errore salvataggio');
                                        }
                                    }}
                                />
                            )}
                        </Suspense>
                    </div>
                )}
            </div>
        </>
    );
}
